import { useContext, useEffect, useRef } from 'react';
import { number, object } from 'yup';

import { CustomCheckbox, Select } from '@/molecules';

import { InitPropsProvider } from '@/components/providers';
import { isValidVariation } from '@/lib/utils';
// WARNING
// If the import is from @/lib/utils this break getPathsFromIndex
// TODO: try to fix this
import { FORMS_ERROS } from '@/lib/utils/forms';

import type { TypeBundle } from '@/components/providers';
import type { SelectProps } from '@/molecules/input/types';
import type { FormikProps, FormikValues } from 'formik';
import type { GenerateAllAtributes } from './types';

export const QuantitySchema = object().shape({
  cantidad: number().required(FORMS_ERROS.required),
});

export const Quantity = ({
  minQuantity = 1,
  exactQuantity,
  ...props
}: Omit<SelectProps, 'options' | 'name' | 'id'> & {
  minQuantity?: number;
  exactQuantity?: number[];
}) => {
  const {
    options: { numMaxProduct },
  } = useContext(InitPropsProvider);

  let select_options;
  if (exactQuantity && exactQuantity.length > 0) {
    select_options = Array.from(exactQuantity, (v) => ({
      value: v,
      label: v,
    }));
  } else {
    select_options = Array.from(
      {
        length:
          minQuantity > numMaxProduct ? 1 : numMaxProduct + 1 - minQuantity,
      },
      (_, k) => ({
        value: k + minQuantity,
        label: k + minQuantity,
      }),
    );
  }

  return (
    <Select name="cantidad" options={select_options} id="cantidad" {...props} />
  );
};

export const useProductForm = <T extends FormikValues>({
  productId,
  quantity = 1,
}: {
  productId: string | number;
  quantity?: number;
}) => {
  const mobile = useRef<FormikProps<T>>(null);
  const desktop = useRef<FormikProps<T>>(null);

  useEffect(() => {
    // We only update the form on view
    // The query string needs to match forms display breakpoint
    if (window.matchMedia('(min-width: 1024px)').matches) {
      if (desktop.current) desktop.current.setFieldValue('cantidad', quantity);
    } else {
      if (mobile.current) mobile.current.setFieldValue('cantidad', quantity);
    }
  }, [productId, quantity]);

  return { formRef: { mobile, desktop } };
};

const generateOptions = ({
  formRef,
  variations,
  item,
  registerField,
  index,
}: Pick<GenerateAllAtributes, 'registerField' | 'variations' | 'formRef'> & {
  item: GenerateAllAtributes['allAtributes'][number];
  index: number;
}) => {
  const options =
    item.values?.map((option: string) => ({
      label: option,
      value: option,
      disabled:
        index === 0
          ? false
          : !isValidVariation(formRef, variations, option, item.key),
    })) ?? [];

  registerField(item.key, {
    validate: (v) =>
      !v || v.length < 1 ? 'Debes Seleccionar un valor' : undefined,
  });

  return options;
};

export const generateAttributes = ({
  allAtributes,
  errors,
  registerField,
  variations,
  formRef,
}: GenerateAllAtributes) =>
  allAtributes.length > 0 && (
    <div className="flex flex-col gap-4 border-t border-dashed border-primary-900 py-4">
      {allAtributes?.map((item: any, itemIndex: number) => {
        const options = generateOptions({
          formRef,
          variations,
          item,
          registerField,
          index: itemIndex,
        });

        return (
          <div
            key={`${item.key}-${itemIndex}`}
            className="flex items-center justify-start"
          >
            <h4 className="u-headline u-headline--h4 mr-5 whitespace-nowrap">
              {item.niceName}
            </h4>
            <CustomCheckbox
              name={item.key}
              id={item.key}
              values={options}
              error={errors[item.key as keyof typeof errors]}
              testid="custom"
            />
            {errors && (
              <p className="u-label u-label--xs ml-auto text-status-error">
                {errors[item.key as keyof typeof errors]}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );

export const generateAttributesMobile = ({
  allAtributes,
  errors,
  registerField,
  variations,
  formRef,
}: GenerateAllAtributes) =>
  allAtributes?.map((item: any, itemIndex: number) => {
    const options = generateOptions({
      formRef,
      variations,
      item,
      registerField,
      index: itemIndex,
    });

    return (
      <Select
        key={`${item.key}-${itemIndex}`}
        name={item.key}
        options={options}
        placeholder={item.niceName}
        id={item.key}
        error={errors[item.key as keyof typeof errors]}
        classNameSelect="!h-full !text-base [&_span]:truncate"
        theme="tertiary"
        reverse
      />
    );
  });

export const generateBundleMetadata = (bundle: TypeBundle) => [
  { woosb_ids: bundle.woobsIds },
  {
    woosb_variant_quantity:
      bundle.selectedVariant?.quantity ?? bundle.numProducts ?? null,
  },
  { woobs_variant_discount_type: bundle.discount.type },
  { woobs_variant_discount_amount: bundle.discount.amount },
  { woobs_variant_discount_percentage: bundle.discount.percentage },
];
